import React, { useEffect, useRef, useState } from 'react'
import {
  getAccountWatermark,
  setWatermarkPosition,
  toggleWatermark,
  ImageWatermarkSetting,
  WatermarkAnchor,
  WatermarkOverride,
} from './core/_requests'

// Per-account image-watermark editor: toggle the watermark on for this client and
// override its position + SIZE (logo / text), with a live preview that mirrors the
// admin-settings preview. Self-contained — fetches the account's state and the
// global config by id, so it can be dropped into any admin screen (account page,
// payments settings modal, …). The global appearance (logo image, text, colour,
// opacity) is still managed centrally in admin settings; here we only override
// position/size for the one account.

const WATERMARK_ANCHORS: WatermarkAnchor[] = [
  'top-left', 'top-center', 'top-right',
  'middle-left', 'middle-center', 'middle-right',
  'bottom-left', 'bottom-center', 'bottom-right',
]

// CSS to place the preview watermark block at the given anchor + offsets, mirroring
// the server-side math (a centered axis ignores its offset).
const anchorPreviewStyle = (anchor: string, offX: number, offY: number): React.CSSProperties => {
  const [v, h] = (anchor || 'bottom-right').split('-')
  const s: React.CSSProperties = {}
  let tx = '0', ty = '0'
  if (h === 'left') s.left = `${offX}%`
  else if (h === 'right') s.right = `${offX}%`
  else { s.left = '50%'; tx = '-50%' }
  if (v === 'top') s.top = `${offY}%`
  else if (v === 'bottom') s.bottom = `${offY}%`
  else { s.top = '50%'; ty = '-50%' }
  if (tx !== '0' || ty !== '0') s.transform = `translate(${tx}, ${ty})`
  return s
}

const WatermarkOverridePanel: React.FC<{ accountId: string; onEnabledChange?: (enabled: boolean) => void }> = ({ accountId, onEnabledChange }) => {
  const [loading, setLoading] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [globalCfg, setGlobalCfg] = useState<ImageWatermarkSetting | null>(null)
  const [ov, setOv] = useState<WatermarkOverride>({})
  const [busy, setBusy] = useState(false)
  // Live slider drafts so dragging doesn't fire a PUT per tick (committed on release).
  const [drafts, setDrafts] = useState<Partial<Record<keyof WatermarkOverride, number>>>({})
  const [sampleImage, setSampleImage] = useState('')
  const [previewWidth, setPreviewWidth] = useState(0)
  const previewRef = useRef<HTMLDivElement>(null)
  const sampleInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getAccountWatermark(accountId)
      .then((res) => {
        if (cancelled) return
        setEnabled(!!res.data.enabled)
        setGlobalCfg(res.data.global)
        setOv(res.data.override || {})
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [accountId])

  // Track preview width so logo/text sizes can be derived from image-width percentages.
  useEffect(() => {
    const el = previewRef.current
    if (!el) return
    const update = () => setPreviewWidth(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [enabled, loading])

  const handleToggle = async () => {
    const next = !enabled
    setBusy(true)
    try {
      await toggleWatermark(accountId, next)
      setEnabled(next)
      onEnabledChange?.(next)
    } catch (e: any) {
      alert('שגיאה בעדכון חתימת לוגו: ' + (e?.response?.data?.error || e?.message || 'Unknown error'))
    } finally {
      setBusy(false)
    }
  }

  // Send the full current override (merged with a patch) — the server replaces the
  // whole override object each call, so all set fields must be included every time.
  const commit = async (patch: WatermarkOverride) => {
    const next = { ...ov, ...patch }
    setOv(next)
    setBusy(true)
    try {
      await setWatermarkPosition(accountId, {
        anchor: next.anchor,
        offsetXPercent: next.offsetXPercent,
        offsetYPercent: next.offsetYPercent,
        logoSizePercent: next.logoSizePercent,
        textSizePercent: next.textSizePercent,
      })
    } catch (e: any) {
      alert('שגיאה בעדכון חתימת לוגו: ' + (e?.response?.data?.error || e?.message || 'Unknown error'))
    } finally {
      setBusy(false)
    }
  }

  const clearOverride = async () => {
    setOv({})
    setDrafts({})
    setBusy(true)
    try {
      await setWatermarkPosition(accountId, { clear: true })
    } catch (e: any) {
      alert('שגיאה באיפוס חתימת לוגו: ' + (e?.response?.data?.error || e?.message || 'Unknown error'))
    } finally {
      setBusy(false)
    }
  }

  const handleSampleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    const r = new FileReader()
    r.onload = () => setSampleImage(String(r.result || ''))
    r.readAsDataURL(f)
  }

  if (loading) return <div style={{ fontSize: 12, color: '#9ca3af' }}>טוען חתימת לוגו…</div>

  const g = globalCfg
  // Effective values for the preview: live draft while dragging, else the account
  // override field when set, else the global value.
  const eff = {
    anchor: (ov.anchor ?? g?.anchor ?? 'bottom-right') as string,
    offsetXPercent: drafts.offsetXPercent ?? ov.offsetXPercent ?? g?.offsetXPercent ?? 3,
    offsetYPercent: drafts.offsetYPercent ?? ov.offsetYPercent ?? g?.offsetYPercent ?? 3,
    logoSizePercent: drafts.logoSizePercent ?? ov.logoSizePercent ?? g?.logoSizePercent ?? 18,
    textSizePercent: drafts.textSizePercent ?? ov.textSizePercent ?? g?.textSizePercent ?? 4,
    opacity: g?.opacity ?? 90,
    imageUrl: g?.imageUrl || '',
    text: g?.text || '',
    textColor: g?.textColor || '#ffffff',
  }
  const isOverridden = (k: keyof WatermarkOverride) => ov[k] !== undefined && ov[k] !== null
  const hasAnyOverride = ['anchor', 'offsetXPercent', 'offsetYPercent', 'logoSizePercent', 'textSizePercent']
    .some((k) => isOverridden(k as keyof WatermarkOverride))

  // All four numeric controls share the draft-on-drag, commit-on-release pattern so a
  // drag fires a single PUT instead of one per tick.
  const setDraft = (key: keyof WatermarkOverride, n: number | null) =>
    setDrafts((d) => { const next = { ...d }; if (n == null) delete next[key]; else next[key] = n; return next })
  const releaseSlider = (key: keyof WatermarkOverride, value: number) =>
    commit({ [key]: value } as WatermarkOverride).then(() => setDraft(key, null))

  const sliders: { key: 'offsetXPercent' | 'offsetYPercent' | 'logoSizePercent' | 'textSizePercent'; label: string; min: number; max: number; val: number }[] = [
    { key: 'offsetXPercent', label: 'מרחק אופקי מהקצה (%)', min: 0, max: 50, val: eff.offsetXPercent },
    { key: 'offsetYPercent', label: 'מרחק אנכי מהקצה (%)', min: 0, max: 50, val: eff.offsetYPercent },
    { key: 'logoSizePercent', label: 'גודל הלוגו (% מרוחב התמונה)', min: 2, max: 60, val: eff.logoSizePercent },
    { key: 'textSizePercent', label: 'גודל הטקסט (% מרוחב התמונה)', min: 1, max: 20, val: eff.textSizePercent },
  ]

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, background: '#fff' }}>
      {/* Enable toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e1e2d' }}>
            <i className="bi bi-badge-wc" style={{ marginLeft: 6, color: '#2b70fa' }}></i>
            חתימת לוגו על תמונות
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
            הלוגו והטקסט נקבעים בהגדרות המערכת. כאן מפעילים ללקוח הזה וקובעים מיקום וגודל.
          </div>
        </div>
        <div
          onClick={() => !busy && handleToggle()}
          title="הפעלת חתימת לוגו ללקוח זה"
          style={{
            width: 44, height: 24, borderRadius: 12, position: 'relative', flexShrink: 0,
            background: enabled ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : '#e4e6ef',
            cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1, transition: 'all 0.2s',
          }}
        >
          <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, right: enabled ? 2 : 22, transition: 'all 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
      </div>

      {enabled && (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, marginTop: 14 }}>
          {/* Controls */}
          <div>
            {/* Anchor grid */}
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>מיקום</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 34px)', gap: 4 }}>
              {WATERMARK_ANCHORS.map((a) => {
                const on = eff.anchor === a
                return (
                  <button
                    key={a}
                    type="button"
                    title={a}
                    disabled={busy}
                    onClick={() => commit({ anchor: a })}
                    style={{ height: 30, borderRadius: 6, cursor: busy ? 'not-allowed' : 'pointer', padding: 0, border: on ? '2px solid #2b70fa' : '1px solid #d1d5db', background: on ? '#2b70fa' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: 2, background: on ? '#fff' : '#9ca3af', display: 'block' }} />
                  </button>
                )
              })}
            </div>

            {/* Offset + size sliders (commit on release) */}
            {sliders.map(({ key, label, min, max, val }) => (
              <div key={key} style={{ marginTop: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}{isOverridden(key) && <span style={{ color: '#2b70fa', marginRight: 4 }}>•</span>}</label>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#2b70fa', direction: 'ltr' }}>{val}%</span>
                </div>
                <input
                  type="range" min={min} max={max} step={1} value={val} disabled={busy}
                  onChange={(e) => setDraft(key, Number(e.target.value))}
                  onMouseUp={(e) => releaseSlider(key, Number((e.target as HTMLInputElement).value))}
                  onTouchEnd={(e) => releaseSlider(key, Number((e.target as HTMLInputElement).value))}
                  style={{ width: '100%', accentColor: '#f59e0b', direction: 'ltr' }}
                />
              </div>
            ))}

            {hasAnyOverride && (
              <button type="button" disabled={busy} onClick={clearOverride} style={{ marginTop: 14, fontSize: 12, color: '#dc2626', background: 'transparent', border: 'none', cursor: busy ? 'not-allowed' : 'pointer', padding: 0 }}>
                <i className="bi bi-arrow-counterclockwise" style={{ marginLeft: 4 }}></i>
                איפוס לערכים הגלובליים
              </button>
            )}
          </div>

          {/* Preview */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>תצוגה מקדימה</label>
              <input ref={sampleInputRef} type="file" accept="image/*" onChange={handleSampleUpload} style={{ display: 'none' }} />
              <button onClick={() => sampleInputRef.current?.click()} style={{ padding: '3px 10px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                <i className="bi bi-image" style={{ marginLeft: 6 }}></i>
                תמונת דוגמה
              </button>
            </div>
            <div
              ref={previewRef}
              style={{
                position: 'relative', width: '100%', aspectRatio: '1 / 1', borderRadius: 10,
                border: '1px solid #e5e7eb', overflow: 'hidden',
                background: sampleImage ? `center / cover no-repeat url(${sampleImage})` : 'linear-gradient(135deg, #64748b 0%, #334155 60%, #1e293b 100%)',
              }}
            >
              {!sampleImage && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
                  תמונת המוצר תופיע כאן
                </div>
              )}
              <div style={{
                position: 'absolute',
                ...anchorPreviewStyle(eff.anchor, eff.offsetXPercent, eff.offsetYPercent),
                opacity: eff.opacity / 100,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: `${Math.max(2, previewWidth * 0.008)}px`, pointerEvents: 'none',
              }}>
                {eff.imageUrl && (
                  <img src={eff.imageUrl} alt="watermark" style={{ width: `${(previewWidth * eff.logoSizePercent) / 100}px`, height: 'auto', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }} />
                )}
                {eff.text && (
                  <span style={{ fontSize: `${(previewWidth * eff.textSizePercent) / 100}px`, color: eff.textColor, fontWeight: 700, lineHeight: 1.1, whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.55)', direction: 'rtl' }}>
                    {eff.text}
                  </span>
                )}
              </div>
            </div>
            <p style={{ margin: '8px 0 0', fontSize: 11, color: '#9ca3af', lineHeight: 1.5 }}>
              המיקום והגדלים מחושבים באחוזים ממידות התמונה. נקודה כחולה (•) = ערך שנקבע ספציפית ללקוח זה.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default WatermarkOverridePanel
